import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { fetchPropertyAndMedia, fetchProperty } from "./trestlepropertyapi";
import axiosInstance, { URL } from "../utils/axios";
import { DialogMaximize } from "../components/Form/Dialog";
import toast from "react-hot-toast";
import { useProperties } from "../hooks/useProperty";
import { PropertyFormFields } from "./PropertyForm";
import { EMPTY_FORM_DATA, validatePropertyForm, deriveCwStatusFromMls, mlsTotalBathrooms } from "../constants/propertyFields";
import { loadDraft, saveDraft, clearDraft } from "../utils/formDraft";

// Draft key + the pristine (empty) state for a fresh "Add For Sale". Anything
// that differs from PRISTINE is treated as unsaved data worth persisting/confirming.
const DRAFT_NAME = "add-sale";
const PRISTINE = {
  ...EMPTY_FORM_DATA,
  listing_type: "Residential Sale",
  cw_listing_status: "ACTIVE",
  status: "Sale",
  state: "FL",
};

const PropertyForm = ({ isOpen, onClose, title }) => {
  const [showMLSModal, setShowMLSModal] = useState(false);
  const [showMLSInputModal, setShowMLSInputModal] = useState(false);
  const [showMainModal, setShowMainModal] = useState(false);
  const [ListingId, setListingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const { propertyAddLoading, propertyAddSuccess, propertyAdd } =
    useProperties();

  // Recover any unsaved draft from a previous session/reload; otherwise start clean.
  const [formData, setFormData] = useState(() => loadDraft(DRAFT_NAME) || { ...PRISTINE });
  const restoredDraftRef = useRef(loadDraft(DRAFT_NAME) != null);

  // True when the form holds anything the user would not want to silently lose.
  const isDirty = () => JSON.stringify(formData) !== JSON.stringify(PRISTINE);

  const resetForm = () => {
    setFormData({ ...PRISTINE });
    clearDraft(DRAFT_NAME);
  };

  // Close the form, confirming first if there is unsaved data so an accidental
  // dismissal can't wipe everything the user entered.
  const requestClose = () => {
    if (isDirty() && !window.confirm("Discard changes? Any information you entered will be lost.")) {
      return;
    }
    resetForm();
    onClose();
  };

  // Persist the draft on every change so the data survives reload / navigation /
  // unmount; clear it once the form is back to pristine.
  useEffect(() => {
    if (JSON.stringify(formData) !== JSON.stringify(PRISTINE)) {
      saveDraft(DRAFT_NAME, formData);
    } else {
      clearDraft(DRAFT_NAME);
    }
  }, [formData]);

  useEffect(() => {
    if (isOpen) {
      if (restoredDraftRef.current) {
        // Recovered work: jump straight to the filled form and tell the user.
        restoredDraftRef.current = false;
        setShowMLSModal(false);
        setShowMLSInputModal(false);
        setShowMainModal(true);
        toast.success("Restored your unsaved draft.");
      } else {
        setShowMLSModal(true);
        setShowMLSInputModal(false);
        setShowMainModal(false);
      }
    }
  }, [isOpen]);

  const handleMLSModalClose = (hasMLS) => {
    setShowMLSModal(false);
    if (hasMLS) {
      setShowMLSInputModal(true);
    } else {
      setShowMainModal(true);
    }
  };

  const handleFetchProperty = async () => {
    if (!ListingId) {
      toast.error("Please enter an MLS number");
      return;
    }
    setLoading(true);
    try {
      const property = await fetchProperty(ListingId);
      if (!property) {
        // fetchProperty resolves to null (it doesn't throw) when the listing isn't found.
        // Keep the MLS # the agent typed and let them fill the rest in by hand.
        setFormData((prev) => ({ ...prev, mls_number: String(ListingId).trim() }));
        setShowMLSInputModal(false);
        setShowMainModal(true);
        toast.error(`No MLS listing found for ${ListingId}. Continuing with manual entry.`);
        return;
      }
      // Map MLS data to form fields (only spec-compliant fields)
      const mappedData = {};
      mappedData.mls_number = String(ListingId).trim(); // Store MLS #
      if (property.StreetNumber) mappedData.street_number = String(property.StreetNumber);
      if (property.StreetDirPrefix) mappedData.compass_direction = property.StreetDirPrefix;
      if (property.StreetName) mappedData.street_name = property.StreetName;
      if (property.StreetDirSuffix) mappedData.post_street_direction = property.StreetDirSuffix;
      if (property.UnitNumber) mappedData.unit_number = property.UnitNumber;
      if (property.City) mappedData.city = property.City;
      if (property.StateOrProvince) mappedData.state = property.StateOrProvince;
      if (property.PostalCode) mappedData.zip_code = property.PostalCode;
      if (property.CountyOrParish) mappedData.county_name = property.CountyOrParish;
      if (property.BedroomsTotal) mappedData.bedrooms = property.BedroomsTotal;
      const totalBaths = mlsTotalBathrooms(property);
      if (totalBaths !== null) mappedData.bathrooms = totalBaths;
      if (property.BathroomsHalf) mappedData.half_bathrooms = property.BathroomsHalf;
      if (property.LivingArea) mappedData.sf_under_ac = property.LivingArea;
      if (property.YearBuilt) mappedData.year_built = property.YearBuilt;
      if (property.ListPrice) mappedData.price_or_rent = property.ListPrice;
      if (property.PropertyType) {
        const typeMap = {
          "Single Family Residence": "SFH",
          Condominium: "Condo",
          Townhouse: "TH",
          Villa: "Villa",
        };
        mappedData.home_type = typeMap[property.PropertyType] || "";
      }

      setFormData((prev) => ({ ...prev, ...mappedData }));
      setShowMLSInputModal(false);
      setShowMainModal(true);
      toast.success("Property data loaded from MLS");
    } catch (error) {
      toast.error("Failed to fetch property from MLS");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mls_listing_status") {
      setFormData((prev) => {
        const derived =
          deriveCwStatusFromMls(value, prev.listing_type) ??
          prev.cw_listing_status ??
          "ACTIVE";
        return { ...prev, mls_listing_status: value, cw_listing_status: derived, status: derived === "SOLD" ? "Sold" : "Sale" };
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const numericFields = ["subdivision_id", "year_built", "bedrooms", "bathrooms", "half_bathrooms", "sf_under_ac"];
  const decimalFields = ["price_or_rent", "sold_price"];

  const formatFormData = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        numericFields.includes(key)
          ? value === "" || value === null ? null : parseInt(value, 10) || null
          : decimalFields.includes(key)
            ? value === "" || value === null ? null : parseFloat(value) || null
            : Array.isArray(value)
              ? JSON.stringify(value)
              : value === "" ? null : value,
      ])
    );
  };

  const validateForm = () => validatePropertyForm(formData);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error("Please fix the validation errors");
      return;
    }
    setValidationErrors([]);

    try {
      const formattedData = formatFormData(formData);
      propertyAdd(formattedData);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSaveIncomplete = async () => {
    setValidationErrors([]);
    try {
      const formattedData = formatFormData({ ...formData, cw_listing_status: "INCOMPLETE", saveIncomplete: true });
      propertyAdd(formattedData);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (propertyAddSuccess) {
      resetForm();
      onClose();
    }
  }, [propertyAddSuccess]);

  return (
    <>
      {/* MLS Question Modal */}
      {isOpen && showMLSModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative bg-white dark:bg-gray-800 w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
            <button
              onClick={requestClose}
              className="absolute top-4 right-4 text-red hover:text-red-500"
            >
              <Icon icon="bx:x-circle" fontSize={24} />
            </button>
            <h3 className="text-lg font-bold mb-4">
              Do you have an MLS number?
            </h3>
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => handleMLSModalClose(true)}
                className="bg-red-700 text-white px-4 py-2 rounded"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => handleMLSModalClose(false)}
                className="bg-gray-800 text-white px-4 py-2 rounded-md"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MLS Input Modal */}
      {isOpen && showMLSInputModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative bg-white dark:bg-gray-800 w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
            <button
              onClick={requestClose}
              className="absolute top-4 right-4 text-red hover:text-red-500"
            >
              <Icon icon="bx:x-circle" fontSize={24} />
            </button>
            <h3 className="text-lg font-bold mb-4">Enter your MLS number</h3>
            <input
              type="text"
              value={ListingId}
              onChange={(e) => setListingId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded mb-4"
              placeholder="MLS Number"
            />
            <div className="flex justify-end">
              {loading ? (
                <div className="mt-2 text-red-700">
                  <svg
                    className="animate-spin h-5 w-5 text-red-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 32 32"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                </div>
              ) : (
                <button
                  onClick={handleFetchProperty}
                  className="bg-red-700 text-white px-4 py-2 rounded"
                  disabled={loading}
                >
                  Search
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Form Modal */}
      {showMainModal && (
        <DialogMaximize
          visible={isOpen}
          header={title}
          onClose={requestClose}
          body={
            <form
              onSubmit={handleSubmit}
              className="max-w-2xl mx-auto p-6 bg-white relative"
            >
              {/* Floating Submit Button */}
              <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={requestClose}
                    className="bg-[#50565aa4] text-white px-4 py-2 rounded hover:bg-[#50565a]"
                  >
                    Cancel
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={propertyAddLoading}
                      onClick={handleSaveIncomplete}
                      className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      {propertyAddLoading ? "Saving..." : "Save Incomplete"}
                    </button>
                    <button
                      type="submit"
                      disabled={propertyAddLoading}
                      className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      {propertyAddLoading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </div>
              </div>

              <PropertyFormFields
                formData={formData}
                handleChange={handleChange}
                mode="add-sale"
                errors={validationErrors}
              />

              <div className="sticky bottom-0 z-10 bg-white pt-4 mt-4 border-t border-gray-200 flex justify-between">
                <button
                  type="button"
                  onClick={() => onClose()}
                  className="bg-[#50565aa4] text-white px-4 py-2 rounded hover:bg-[#50565a]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={propertyAddLoading}
                  className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {propertyAddLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          }
        />
      )}
    </>
  );
};

export default PropertyForm;
